import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkle, Check, X, ArrowRight, Plus } from "@phosphor-icons/react";

/**
 * AI suggestions for unmapped lead sources. Two kinds:
 *  - `map`: "raw → canonical (82%)" — Accept adds the alias and recanonicalizes.
 *  - `new`: a proposed brand-new canonical source grouping several raw variants
 *    as aliases — admin can rename before creating.
 */
export default function SourceSuggestionsPanel({ suggestions, onResolved, onResolvedAll }) {
  const [busyId, setBusyId] = useState(null);
  const [names, setNames] = useState({});

  if (!suggestions?.length) return null;

  const acceptAll = async () => {
    const proposals = suggestions.filter((s) => s.kind === "new").length;
    const maps = suggestions.length - proposals;
    if (!window.confirm(
      `Create ${proposals} new source${proposals === 1 ? "" : "s"} and accept ${maps} mapping${maps === 1 ? "" : "s"}? `
      + "Aliases are learned and every lead is reclassified."
    )) return;
    setBusyId("all");
    try {
      const edited = Object.fromEntries(Object.entries(names).filter(([, v]) => v && v.trim()));
      const { data } = await api.post("/sources/suggestions/accept-all", { names: edited });
      toast.success(`Applied ${data.accepted} suggestion${data.accepted === 1 ? "" : "s"} — ${data.leads_updated} lead(s) reclassified`
        + (data.skipped?.length ? ` (${data.skipped.length} skipped)` : ""));
      onResolvedAll?.(data.sources, data.skipped || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk accept failed");
    } finally {
      setBusyId(null);
    }
  };

  const act = async (s, action) => {
    setBusyId(s.id);
    try {
      if (action === "accept") {
        const body = s.kind === "new" ? { name: (names[s.id] ?? s.name).trim() } : {};
        const { data } = await api.post(`/sources/suggestions/${s.id}/accept`, body);
        toast.success(s.kind === "new"
          ? `Created source "${body.name}" — ${data.leads_updated} lead(s) reclassified`
          : `"${s.raw}" now maps to ${s.canonical} — ${data.leads_updated} lead(s) reclassified`);
        onResolved?.(s.id, data.sources);
      } else {
        await api.post(`/sources/suggestions/${s.id}/reject`);
        onResolved?.(s.id, null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const proposals = suggestions.filter((s) => s.kind === "new");
  const mappings = suggestions.filter((s) => s.kind === "map");

  return (
    <section data-testid="source-suggestions-panel" className="bg-indigo-50/60 border border-indigo-200 rounded-sm p-6 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkle size={16} className="text-indigo-700" weight="fill" />
        <div className="label-tracking !text-indigo-900">{suggestions.length} AI suggestion{suggestions.length === 1 ? "" : "s"} awaiting review</div>
        <Button
          data-testid="suggestions-accept-all-btn"
          size="sm"
          disabled={busyId !== null}
          onClick={acceptAll}
          className="ml-auto h-8 rounded-sm gap-1.5 bg-indigo-700 hover:bg-indigo-800"
        >
          <Check size={12} /> {busyId === "all" ? "Applying…" : `Accept & create all (${suggestions.length})`}
        </Button>
      </div>

      {proposals.length > 0 && (
        <div className="space-y-2">
          <div className="text-[0.6rem] uppercase tracking-wider text-indigo-700">Proposed new sources</div>
          {proposals.map((s) => (
            <div key={s.id} data-testid={`suggestion-new-${s.id}`} className="bg-white border border-indigo-200 rounded-sm p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  data-testid={`suggestion-new-name-${s.id}`}
                  value={names[s.id] ?? s.name}
                  onChange={(e) => setNames((m) => ({ ...m, [s.id]: e.target.value }))}
                  className="h-8 rounded-sm text-sm font-medium w-64"
                />
                <span className="text-[0.65rem] text-slate-500">{s.count} lead{s.count === 1 ? "" : "s"}</span>
                <div className="ml-auto inline-flex items-center gap-1.5">
                  <Button data-testid={`suggestion-accept-${s.id}`} size="sm" disabled={busyId !== null} onClick={() => act(s, "accept")} className="h-8 rounded-sm gap-1 bg-indigo-700 hover:bg-indigo-800">
                    <Plus size={12} /> {busyId === s.id ? "Creating…" : "Create source"}
                  </Button>
                  <Button data-testid={`suggestion-reject-${s.id}`} size="sm" variant="ghost" disabled={busyId !== null} onClick={() => act(s, "reject")} className="h-8 rounded-sm text-slate-500">
                    <X size={12} /> Dismiss
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(s.aliases || []).map((a) => (
                  <span key={a} className="bg-slate-50 border border-slate-200 rounded-sm px-2 py-0.5 text-xs font-mono">{a}</span>
                ))}
              </div>
              {s.reason && <p className="text-xs text-slate-500">{s.reason}</p>}
            </div>
          ))}
        </div>
      )}

      {mappings.length > 0 && (
        <div className="space-y-2">
          <div className="text-[0.6rem] uppercase tracking-wider text-indigo-700">Suggested mappings</div>
          {mappings.map((s) => (
            <div key={s.id} data-testid={`suggestion-map-${s.id}`} className="bg-white border border-indigo-200 rounded-sm p-3 flex items-center gap-3 flex-wrap">
              <span className="font-mono text-xs text-slate-800 break-all">{s.raw}</span>
              <ArrowRight size={12} className="text-slate-400" />
              <span className="text-sm font-medium text-slate-900">{s.canonical}</span>
              <span className="text-[0.65rem] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">{Math.round((s.confidence || 0) * 100)}% · {s.count} lead{s.count === 1 ? "" : "s"}</span>
              {s.reason && <span className="text-xs text-slate-500 flex-1 min-w-[200px]">{s.reason}</span>}
              <div className="ml-auto inline-flex items-center gap-1.5">
                <Button data-testid={`suggestion-accept-${s.id}`} size="sm" disabled={busyId !== null} onClick={() => act(s, "accept")} className="h-8 rounded-sm gap-1 bg-indigo-700 hover:bg-indigo-800">
                  <Check size={12} /> {busyId === s.id ? "Applying…" : "Accept"}
                </Button>
                <Button data-testid={`suggestion-reject-${s.id}`} size="sm" variant="ghost" disabled={busyId !== null} onClick={() => act(s, "reject")} className="h-8 rounded-sm text-slate-500">
                  <X size={12} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
